import { DatePipe, DecimalPipe, KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AdminLiveSessionService,
  AdminSessionDetail,
  AdminSessionAttendance,
  AdminRecordingInfo,
  AdminChatMessage,
  AdminSessionAnalytics,
  BulkRefundPreview,
  CustomRefundPayload,
  NotificationLog,
  PurchaseContext,
  RefundHistoryEntry,
  ScheduledReminder,
  SessionRefundReason,
  SessionRefundResponse,
} from '@shared/services/live-sessions';
import { ThkVideoPlayerComponent } from '../../../@shared/components/thk-video-player';
import { Subject, Subscription } from 'rxjs';
import { PaginationService, VideoData } from 'thkee-common';
import { CommonPaginationComponent } from '../../../@shared/components/common-pagination/common-pagination.component';
import { DrawerComponent } from '../../../@shared/components/drawer/drawer.component';
import { ProcessingDirective } from '../../../../../../thkee-common/src/lib/directives/processing.directive';

type SessionTab = 'overview' | 'participants' | 'notifications' | 'reminders' | 'refund-history' | 'recording' | 'analytics';

@Component({
  selector: 'app-admin-session-detail',
  templateUrl: './admin-session-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    DecimalPipe,
    KeyValuePipe,
    FormsModule,
    CommonPaginationComponent,
    DrawerComponent,
    ProcessingDirective,
    ThkVideoPlayerComponent,
  ],
})
export class AdminSessionDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly adminService = inject(AdminLiveSessionService);
  private readonly paginationService = inject(PaginationService);

  sessionId = 0;
  session = signal<AdminSessionDetail | null>(null);
  notifications = signal<NotificationLog[]>([]);
  reminders = signal<ScheduledReminder[]>([]);
  totalNotifications = signal(0);

  // Recording & Chat
  recordingInfo = signal<AdminRecordingInfo | null>(null);
  chatMessages = signal<AdminChatMessage[]>([]);
  totalChatMessages = signal(0);
  isLoadingRecording = signal(false);
  isLoadingChat = signal(false);
  chatParams = { page: 1, page_size: 50 };
  videoSources$ = new Subject<VideoData>();

  // Analytics
  analytics = signal<AdminSessionAnalytics | null>(null);
  isLoadingAnalytics = signal(false);

  isLoading = signal(false);
  isNotificationsLoading = signal(false);
  isRemindersLoading = signal(false);
  isJoining = signal(false);
  isRefunding = signal(false);
  isBulkRefunding = signal(false);

  hasRefundableParticipants = computed(() => {
    const s = this.session();
    if (!s?.participants) return false;
    return s.participants.some(p => !p.refunded);
  });

  activeTab = signal<SessionTab>('overview');
  refundNotes = '';
  selectedAttendance: AdminSessionAttendance | null = null;
  refundResult = signal<SessionRefundResponse | null>(null);
  refundError = signal<string | null>(null);

  // Purchase context state
  purchaseContext = signal<PurchaseContext | null>(null);
  isLoadingContext = signal(false);
  refundHistory = signal<RefundHistoryEntry[]>([]);
  isLoadingRefundHistory = signal(false);

  // Refund reasons
  refundReasons = signal<SessionRefundReason[]>([]);
  selectedReason = 'INSTRUCTOR_NO_SHOW';
  reasonOther = '';

  // Custom refund form
  customRefundAmount = '';
  customRefundMode: 'credit' | 'gateway' = 'credit';
  platformAbsorbs = false;
  fullInstructorClawback = false;
  useCustomAmount = false;
  instructorNote = '';

  notificationParams = { page: 1, page_size: 10 };
  notificationPaginationConfig = { pageCountSummary: true, pageSize: true, firstAndLast: true };

  @ViewChild('refundDrawer') private refundDrawer!: DrawerComponent;
  @ViewChild('bulkRefundDrawer') private bulkRefundDrawer!: DrawerComponent;

  // Bulk refund confirmation state
  bulkRefundPreview = signal<BulkRefundPreview | null>(null);
  isLoadingBulkPreview = signal(false);
  bulkRefundMode: 'credit' | 'gateway' = 'credit';
  bulkPlatformAbsorbs = false;
  bulkSelectedReason = 'INSTRUCTOR_NO_SHOW';
  bulkReasonOther = '';
  bulkRefundNotes = '';
  bulkConfirmInput = '';
  bulkRefundError = signal<string | null>(null);
  bulkRefundSuccess = signal(false);

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.sessionId = Number(this.route.snapshot.paramMap.get('sessionId'));
    this.loadSession();
    this.loadRefundReasons();
  }

  private loadRefundReasons(): void {
    this.subscriptions.add(
      this.adminService.getRefundReasons().subscribe({
        next: (reasons) => this.refundReasons.set(reasons),
      })
    );
  }

  loadSession(): void {
    this.isLoading.set(true);
    this.subscriptions.add(
      this.adminService.getAdminSessionDetail(this.sessionId).subscribe({
        next: (session) => {
          this.session.set(session);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      })
    );
  }

  // Tab switching
  switchTab(tab: SessionTab): void {
    this.activeTab.set(tab);
    if (tab === 'notifications' && this.notifications().length === 0) {
      this.loadNotifications();
    }
    if (tab === 'reminders' && this.reminders().length === 0) {
      this.loadReminders();
    }
    if (tab === 'refund-history' && this.refundHistory().length === 0) {
      this.loadRefundHistory();
    }
    if (tab === 'recording' && this.recordingInfo() === null) {
      this.loadRecording();
      this.loadChat();
    }
    if (tab === 'analytics' && this.analytics() === null) {
      this.loadAnalytics();
    }
  }

  // Load notifications
  loadNotifications(): void {
    this.isNotificationsLoading.set(true);
    this.subscriptions.add(
      this.adminService.getSessionNotifications(this.sessionId, this.notificationParams).subscribe({
        next: (res) => {
          this.notifications.set(res.results);
          this.totalNotifications.set(res.count);
          this.paginationService.getPager(res.count, this.notificationParams.page, this.notificationParams.page_size);
          this.isNotificationsLoading.set(false);
        },
        error: () => this.isNotificationsLoading.set(false),
      })
    );
  }

  // Load reminders
  loadReminders(): void {
    this.isRemindersLoading.set(true);
    this.subscriptions.add(
      this.adminService.getSessionReminders(this.sessionId).subscribe({
        next: (reminders) => {
          this.reminders.set(reminders);
          this.isRemindersLoading.set(false);
        },
        error: () => this.isRemindersLoading.set(false),
      })
    );
  }

  // Join as observer
  joinAsObserver(): void {
    this.isJoining.set(true);
    this.subscriptions.add(
      this.adminService.adminJoinSession(this.sessionId).subscribe({
        next: (res) => {
          sessionStorage.setItem('admin_rtk_token', res.auth_token);
          sessionStorage.setItem('admin_rtk_room', res.room_name);
          window.open(`/live-sessions/meeting/${this.sessionId}`, '_blank');
          this.isJoining.set(false);
        },
        error: () => this.isJoining.set(false),
      })
    );
  }

  // Refund
  openRefundDrawer(attendance: AdminSessionAttendance): void {
    this.selectedAttendance = attendance;
    this.refundResult.set(null);
    this.refundError.set(null);
    this.useCustomAmount = false;
    this.customRefundAmount = '';
    this.customRefundMode = 'credit';
    this.platformAbsorbs = false;
    this.fullInstructorClawback = false;
    this.selectedReason = 'INSTRUCTOR_NO_SHOW';
    this.reasonOther = '';
    this.refundNotes = '';
    this.instructorNote = '';
    this.loadPurchaseContext(attendance.id);
    this.refundDrawer.open();
  }

  submitRefund(): void {
    if (!this.selectedAttendance) return;
    this.isRefunding.set(true);
    this.refundError.set(null);

    this.subscriptions.add(
      this.adminService.refundParticipant(this.sessionId, {
        attendance_id: this.selectedAttendance.id,
        notes: this.refundNotes,
        instructor_note: this.instructorNote,
      }).subscribe({
        next: (res) => {
          this.refundResult.set(res);
          this.isRefunding.set(false);
          // Reload session to reflect refund status
          this.loadSession();
        },
        error: (err) => {
          this.refundError.set(err?.error?.error || err?.error?.detail || 'Refund failed');
          this.isRefunding.set(false);
        },
      })
    );
  }

  openBulkRefundDrawer(): void {
    this.bulkRefundMode = 'credit';
    this.bulkPlatformAbsorbs = false;
    this.fullInstructorClawback = false;
    this.bulkSelectedReason = 'INSTRUCTOR_NO_SHOW';
    this.bulkReasonOther = '';
    this.bulkRefundNotes = '';
    this.instructorNote = '';
    this.bulkConfirmInput = '';
    this.bulkRefundError.set(null);
    this.bulkRefundSuccess.set(false);
    this.loadBulkRefundPreview();
    this.bulkRefundDrawer.open();
  }

  private loadBulkRefundPreview(): void {
    this.isLoadingBulkPreview.set(true);
    this.bulkRefundPreview.set(null);
    this.subscriptions.add(
      this.adminService.refundAllPreview(this.sessionId).subscribe({
        next: (preview) => {
          this.bulkRefundPreview.set(preview);
          this.isLoadingBulkPreview.set(false);
        },
        error: (err) => {
          this.isLoadingBulkPreview.set(false);
          this.bulkRefundError.set(err.error?.detail || err.error?.error || 'Failed to load refund preview');
        },
      })
    );
  }

  get requiresConfirmInput(): boolean {
    return this.bulkRefundMode === 'gateway' &&
      (this.bulkRefundPreview()?.participant_count ?? 0) > 3;
  }

  get canSubmitBulkRefund(): boolean {
    if (this.requiresConfirmInput) {
      return this.bulkConfirmInput === 'CONFIRM';
    }
    return true;
  }

  confirmBulkRefund(): void {
    if (!this.canSubmitBulkRefund) return;
    this.isBulkRefunding.set(true);
    this.bulkRefundError.set(null);

    this.subscriptions.add(
      this.adminService.refundAllParticipants(this.sessionId, {
        refund_mode: this.bulkRefundMode,
        platform_absorbs: this.bulkPlatformAbsorbs,
        full_instructor_clawback: this.fullInstructorClawback,
        reason: this.bulkSelectedReason,
        reason_other: this.bulkSelectedReason === 'OTHER' ? this.bulkReasonOther : '',
        notes: this.bulkRefundNotes,
        instructor_note: this.instructorNote,
      }).subscribe({
        next: () => {
          this.isBulkRefunding.set(false);
          this.bulkRefundSuccess.set(true);
          this.loadSession();
        },
        error: (err) => {
          this.bulkRefundError.set(err.error?.detail || err.error?.error || 'Bulk refund failed');
          this.isBulkRefunding.set(false);
        },
      })
    );
  }

  private loadPurchaseContext(attendanceId: number): void {
    this.isLoadingContext.set(true);
    this.purchaseContext.set(null);
    this.subscriptions.add(
      this.adminService.getPurchaseContext(this.sessionId, attendanceId).subscribe({
        next: (ctx) => {
          this.purchaseContext.set(ctx);
          this.customRefundAmount = ctx.per_session_amount;
          this.isLoadingContext.set(false);
        },
        error: () => this.isLoadingContext.set(false),
      })
    );
  }

  processRefund(): void {
    if (!this.selectedAttendance) return;

    // When no custom options are selected, use the standard refund-participant API
    // which calculates the per-session amount automatically and issues credit.
    const isCustom = this.useCustomAmount
      || this.customRefundMode !== 'credit'
      || this.platformAbsorbs
      || this.fullInstructorClawback;

    if (!isCustom) {
      this.submitRefund();
      return;
    }

    // Custom refund path — admin specified amount, mode, or special options
    this.isRefunding.set(true);
    this.refundError.set(null);

    const payload: CustomRefundPayload = {
      attendance_id: this.selectedAttendance.id,
      refund_amount: this.useCustomAmount ? this.customRefundAmount : (this.purchaseContext()?.per_session_amount ?? '0'),
      refund_mode: this.customRefundMode,
      platform_absorbs: this.platformAbsorbs,
      full_instructor_clawback: this.fullInstructorClawback,
      reason: this.selectedReason,
      reason_other: this.selectedReason === 'OTHER' ? this.reasonOther : '',
      notes: this.refundNotes,
      instructor_note: this.instructorNote,
    };

    this.subscriptions.add(
      this.adminService.customRefund(this.sessionId, payload).subscribe({
        next: (result) => {
          this.refundResult.set(result);
          this.isRefunding.set(false);
          this.loadSession();
        },
        error: (err) => {
          this.refundError.set(err.error?.detail || err.error?.error || 'Refund failed');
          this.isRefunding.set(false);
        },
      })
    );
  }

  loadRefundHistory(): void {
    this.isLoadingRefundHistory.set(true);
    this.subscriptions.add(
      this.adminService.getRefundHistory(this.sessionId).subscribe({
        next: (res) => {
          this.refundHistory.set(res.results);
          this.isLoadingRefundHistory.set(false);
        },
        error: () => this.isLoadingRefundHistory.set(false),
      })
    );
  }

  // Notification pagination
  notificationPage(page: number): void {
    this.notificationParams.page = page;
    this.loadNotifications();
  }

  notificationPageSize(size: number): void {
    this.notificationParams.page_size = size;
    this.notificationParams.page = 1;
    this.loadNotifications();
  }

  // Helpers
  getStatusClass(status: string): string {
    switch (status) {
      case 'sent': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'pending': return 'text-amber-600';
      default: return 'text-neutral-600';
    }
  }

  getReminderStatusClass(status: string): string {
    switch (status) {
      case 'sent': return 'tag-green';
      case 'pending': return 'tag-blue';
      case 'cancelled': return 'tag-red';
      case 'skipped': return 'tag-neutral';
      default: return 'tag-neutral';
    }
  }

  // Load recording info
  loadRecording(): void {
    this.isLoadingRecording.set(true);
    this.subscriptions.add(
      this.adminService.getSessionRecordings(this.sessionId).subscribe({
        next: (info) => {
          this.recordingInfo.set(info);
          this.isLoadingRecording.set(false);
          // Push first recording to video player
          if (info.has_recording && info.recordings.length > 0) {
            const rec = info.recordings[0];
            if (rec.transcoded_urls?.length > 0) {
              this.videoSources$.next({
                video_url: rec.transcoded_urls.map(u => ({ type: u.type, url: u.url })),
                thumbnails: { url: rec.thumbnail || '' },
                thumbnails_vtt: { url: rec.thumbnails_vtt || '' },
              } as VideoData);
            }
          }
        },
        error: () => this.isLoadingRecording.set(false),
      })
    );
  }

  // Load chat messages
  loadChat(): void {
    this.isLoadingChat.set(true);
    this.subscriptions.add(
      this.adminService.getSessionChat(this.sessionId, this.chatParams).subscribe({
        next: (res) => {
          this.chatMessages.set(res.results);
          this.totalChatMessages.set(res.message_count || res.count);
          this.isLoadingChat.set(false);
        },
        error: () => this.isLoadingChat.set(false),
      })
    );
  }

  // Load analytics
  loadAnalytics(): void {
    this.isLoadingAnalytics.set(true);
    this.subscriptions.add(
      this.adminService.getSessionAnalytics(this.sessionId).subscribe({
        next: (data) => {
          this.analytics.set(data);
          this.isLoadingAnalytics.set(false);
        },
        error: () => this.isLoadingAnalytics.set(false),
      })
    );
  }

  // Play a specific recording
  playRecording(index: number): void {
    const info = this.recordingInfo();
    if (!info || !info.recordings[index]) return;
    const rec = info.recordings[index];
    if (rec.transcoded_urls?.length > 0) {
      this.videoSources$.next({
        video_url: rec.transcoded_urls.map(u => ({ type: u.type, url: u.url })),
        thumbnails: { url: rec.thumbnail || '' },
        thumbnails_vtt: { url: rec.thumbnails_vtt || '' },
      } as VideoData);
    }
  }

  formatMinutes(minutes: number | null | undefined): string {
    if (!minutes) return '-';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  goBack(): void {
    this.router.navigate(['/live-sessions/sessions']);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
